import { AxiosError } from "axios";
import BackButton from "components/BackButton";
import Button, { ButtonType } from "components/Elements/Buttonv2";
import Select from "components/Elements/Selectv2/Select";
import React, { ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ApiService from "services/api.service";
import Account from "types/Account";
import TwilioSettings from "./components/TwilioSettings";

export enum WhatsappSendingService {
  Twilio = "twilio",
  VFirst = "vfirst",
}

interface WhatsappSettingsFormData {
  whatsappAccountSid: string;
  whatsappAuthToken: string;
  whatsappFrom: string;
}

export interface SendingServiceSettingsProps {
  formData: WhatsappSettingsFormData;
  setFormData: (value: WhatsappSettingsFormData) => void;
}

const WhatsappSettings = () => {
  const navigate = useNavigate();

  const [sendingService, setSendingService] = useState(
    WhatsappSendingService.VFirst
  );
  const [formData, setFormData] = useState<WhatsappSettingsFormData>({
    whatsappAccountSid: "",
    whatsappAuthToken: "",
    whatsappFrom: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>();

  const sendingServiceToComponentMap = {
    [WhatsappSendingService.VFirst]: (
      <TwilioSettings formData={formData} setFormData={setFormData} />
    ),
    [WhatsappSendingService.Twilio]: (
      <TwilioSettings formData={formData} setFormData={setFormData} />
    ),
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const { data } = await ApiService.get<Account>({ url: "/accounts" });
        const { verified } = data;
        const {
          whatsappAccountSid,
          whatsappAuthToken,
          whatsappFrom,
          whatsappProvider: provider,
        } = data.workspace;
        setFormData({
          whatsappAccountSid: whatsappAccountSid || "",
          whatsappAuthToken: whatsappAuthToken || "",
          whatsappFrom: whatsappFrom || "",
        });
        // @ts-ignore
        setSendingService(provider || sendingService);
      } catch (e) {
        toast.error("Error while loading data");
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const objToSend: Record<string, string> = {};
      for (const key of Object.keys(formData)) {
        if (formData[key as keyof typeof formData])
          objToSend[key] = formData[key as keyof typeof formData];
      }
      await ApiService.patch({
        url: "/accounts",
        options: { ...objToSend, whatsappProvider: sendingService },
      });
      toast.success("Account saved successfully!");
      navigate("/settings");
    } catch (e) {
      let message = "Unexpected error";
      if (e instanceof AxiosError) message = e.response?.data?.message;
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const isError = Boolean(error);
  const isFulfilled = Boolean(
    sendingService === WhatsappSendingService.VFirst
      ? formData.whatsappAccountSid &&
          formData.whatsappAuthToken &&
          formData.whatsappFrom
      : false
  );

  return (
    <div className="p-5 flex justify-center font-inter text-[14px] font-normal leading-[22px] text-[#111827]">
      <div className="max-w-[970px] w-full flex flex-col gap-5">
        <div className="flex gap-[15px] items-center">
          <BackButton />
          <div className="text-[20px] font-semibold leading-[28px] text-black">
            Whatsapp
          </div>
        </div>

        <div className="bg-white p-5 flex flex-col gap-5">
          <div className="flex flex-col gap-[5px]">
            <div>Sending service</div>
            <Select
              id="sending-service-select"
              options={[
                { key: WhatsappSendingService.VFirst, title: "VFirst" },
                { key: WhatsappSendingService.Twilio, title: "Twilio" },
              ]}
              value={sendingService}
              onChange={(value) => setSendingService(value)}
            />
          </div>

          {sendingServiceToComponentMap[sendingService]}
        </div>

        {error && (
          <div className="text-[#E11D48] font-inter text-[12px] leading-5">
            {error}
          </div>
        )}

        <Button
          id="save-Whatsapp-settings-button"
          type={ButtonType.PRIMARY}
          onClick={handleSave}
          className="w-fit"
          disabled={isSaving || isError || !isFulfilled}
        >
          {isSaving ? "Connecting..." : "Connect"}
        </Button>
      </div>
    </div>
  );
};

export default WhatsappSettings;
